import { useState } from 'react';
import { useIntl } from 'react-intl';
import { ExpandableSearch } from '@rossum/components/expandable-search/ExpandableSearch';
import { Filters } from '@rossum/components/filtering';
import {
  FilterSettings,
  FiltersObject,
} from '@rossum/components/filtering/types';
import { IconCustomMenuDown } from '@rossum/ui/icons/tabler';
import {
  Button,
  Menu,
  MenuItem,
  Stack,
  SvgIcon,
  Typography,
} from '@rossum/ui/material';
import { defaultWorkspaceSorting } from '../../../constants/values';
import { SortingValue, sortingValues } from '../../../types/user';

type ToolbarProps<T extends FiltersObject> = {
  searchValue: string;
  onSearchChange: (value: string) => void;
  sorting: SortingValue;
  onSortingChange: (value: SortingValue) => void;
  filters: T;
  setFilters: (filters: T | undefined) => void;
  filterSettings: FilterSettings<T>[];
  isFiltersLoading?: boolean;
};

export const Toolbar = <T extends FiltersObject>({
  searchValue,
  onSearchChange,
  sorting,
  onSortingChange,
  filters,
  setFilters,
  filterSettings,
  isFiltersLoading,
}: ToolbarProps<T>) => {
  const intl = useIntl();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Filters
        dataCy="queue-manager-filters"
        filters={filters}
        setFilters={setFilters}
        filterSettings={filterSettings}
        isLoading={isFiltersLoading}
      />
      <Stack direction="row" alignItems="center" spacing={1}>
        <ExpandableSearch
          value={searchValue}
          onChange={onSearchChange}
          placeholder={intl.formatMessage({
            id: 'features.queueManager.toolbar.searchPlaceholder',
          })}
          shouldCollapseOnBlur
        />
        <Menu
          anchorEl={anchorEl}
          open={!!anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          {sortingValues.map(value => (
            <MenuItem
              key={value}
              selected={value === sorting}
              onClick={() => {
                onSortingChange(value);
                setAnchorEl(null);
              }}
            >
              <Typography variant="body2">
                {intl.formatMessage({
                  id: `components.queueWorkspaceControl.sortingSelect.labels.${value}`,
                })}
              </Typography>
            </MenuItem>
          ))}
        </Menu>
        <Button
          color="secondary"
          data-cy="queue-manager-sort-control"
          onClick={e => setAnchorEl(e.currentTarget)}
          endIcon={
            <SvgIcon fontSize="small">
              <IconCustomMenuDown />
            </SvgIcon>
          }
        >
          {intl.formatMessage({
            id: `components.queueWorkspaceControl.sortingSelect.labels.${
              sorting ?? defaultWorkspaceSorting
            }`,
          })}
        </Button>
      </Stack>
    </Stack>
  );
};
